<template>
  <section class="translation">
    <div class="header">
      <label class="select-language">
        <div class="select-language__label">
          {{ $t('ai.TRANSLATE_FROM') }}
        </div>

        <select
          class="select-language__select"
          aria-label="Select language"
          @change="updateLocale"
        >
          <option
            v-for="locale in locales"
            :key="locale.code"
            :value="locale.code"
          >
            {{ `${locale.name} ${fieldName}` }}
          </option>
        </select>
      </label>

      <HelpTooltip tooltip-position="top">
        <template #icon>
          <EyeIcon class="eye-icon" />
        </template>
        <template #title>
          {{ `${selectedLocale?.name} ${fieldName}` }}
        </template>
        <template #description>
          <p v-sanitize-html="selectedLocale?.text" />
        </template>
      </HelpTooltip>
    </div>

    <AiEntry
      :title="title"
      :content="translatedText"
      :has-ai-text="hasAiText"
      :is-loading="isTranslating"
      @onUseSuggestion="approve"
      @onCancel="cancel"
    />
  </section>
</template>
<script>
import EyeIcon from '@/components/icon_components/EyeIcon'
import { getCorrectLocaleName } from '@/helpers/localeHelper'
import aiService from '@/service/ai.service'
import HelpTooltip from '../HelpTooltip.vue'
import AiEntry from './AiEntry.vue'

export default {
  components: {
    AiEntry,
    EyeIcon,
    HelpTooltip,
  },

  props: {
    localeTranslateTo: {
      type: Object,
      required: true,
      default: () => {},
    },

    locales: {
      type: Array,
      required: true,
    },

    fieldName: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['onCancel', 'onApprove'],
  data() {
    return {
      selectedLocale: this.locales[0],
      translatedText: '',
      isTranslating: false,
    }
  },

  computed: {
    title() {
      const translatedLocaleName = getCorrectLocaleName(this.localeTranslateTo)

      return this.$t('ai.NEW_SUGGESTED_LOCALE_FIELDNAME', {
        localeName: translatedLocaleName,
        fieldName: this.fieldName,
      })
    },

    hasMultipleLocales() {
      return this.locales.length > 1
    },

    hasAiText() {
      return !!this.translatedText
    },
  },

  async mounted() {
    this.translatedText = await this.getTranslation()
  },

  methods: {
    async updateLocale(event) {
      const localeCode = event.target.value
      this.selectedLocale = this.locales.find(
        (locale) => locale.code === localeCode
      )
      this.translatedText = await this.getTranslation()
    },

    async getTranslation() {
      this.isTranslating = true
      try {
        const response = await aiService.getTranslationAi(
          this.selectedLocale.text,
          this.localeTranslateTo.code
        )

        return response.data.text
      } catch (error) {
        this.$toast({ text: error }, { displayDuration: 5000, theme: 'error' })
      } finally {
        this.isTranslating = false
      }
    },

    cancel() {
      this.$emit('onCancel')
    },

    approve() {
      this.$emit('onApprove', this.translatedText)
    },
  },
}
</script>

<style lang="scss" scoped>
.translation {
  display: flex;
  flex-direction: column;
  gap: $spacing-4xs;
  width: 100%;
  margin-top: $spacing-3xs;
}

.header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: $spacing-4xs;
  width: 100%;
}

.select-language {
  width: 100%;

  &__label {
    @include formLabelStyle;
    width: 100%;
  }

  &__select {
    display: flex;
    width: 100%;
    border: 1px solid $color-border;
    padding: $spacing-5xs $spacing-4xs;
    background: $color-canvas;
    border-radius: $spacing-5xs;
    color: $color-text;
    font-size: $font-size-s;
  }
}

.eye-icon {
  background: $color-canvas;
  border-radius: 50%;
  border: 1px solid $color-border;
  padding: $spacing-5xs;
  width: $spacing-m;
  height: $spacing-m;
}
</style>
